import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import { useContextStore } from '@/stores/context.store';
import { useContextHydration } from '@/hooks/useContextHydration';

// Components
import { ProtectedRoute, AdminRoute } from '@/components/auth/ProtectedRoute';
import { SmartRedirect } from '@/components/auth/SmartRedirect';
import { NonPatientRoute } from '@/components/auth/NonPatientRoute';
import { BaseLayout } from '@/components/layout';
import { PageSpinner } from '@/components/ui';

// Pages
import {
  Login,
  PasswordSetup,
  ProfileSettings,
  Dashboard,
  ClinicSelection,
  DoctorSelection
} from '@/pages';
import CalendarPage from '@/pages/CalendarPage';
import SetupWizardPage from '@/pages/SetupWizardPage';
import HelpPage from '@/pages/HelpPage';
import { PatientDashboard } from '@/pages/patient/PatientDashboard';
import DoctorsPage from '@/pages/admin/DoctorsPage';
import UsersPage from '@/pages/admin/UsersPage';
import PatientsPage from '@/pages/admin/PatientsPage';

// Debug component - temporary
function ContextDebugger() {
  const { clinicId, doctorId, clinic, doctor, isHydrated } = useContextStore();
  const { isAuthenticated, isInitialized } = useAuthStore();
  
  // Only show in development
  if (import.meta.env.MODE !== 'development') return null;
  
  return (
    <div style={{
      position: 'fixed',
      bottom: 10,
      right: 10,
      background: 'rgba(0,0,0,0.8)',
      color: 'white',
      padding: '10px',
      fontSize: '12px',
      zIndex: 9999,
      borderRadius: '5px',
      maxWidth: '300px'
    }}>
      <div><strong>Context Debug</strong></div>
      <div>Auth Init: {isInitialized ? '✓' : '✗'}</div>
      <div>Authenticated: {isAuthenticated ? '✓' : '✗'}</div>
      <div>Hydrated: {isHydrated ? '✓' : '✗'}</div>
      <div>Clinic ID: {clinicId || 'null'}</div>
      <div>Clinic: {clinic?.name || 'null'}</div>
      <div>Doctor ID: {doctorId || 'null'}</div>
      <div>Doctor: {doctor?.name || 'null'}</div>
    </div>
  );
}

function App() {
  const { checkAuth, isInitialized, isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Initialize native auth by checking stored tokens
    checkAuth();
  }, [checkAuth]);

  // Hydrate context from localStorage only after auth is initialized
  useContextHydration();

  // Show loading spinner while initializing auth
  if (!isInitialized) {
    return <PageSpinner label="Inicializando aplicação..." />;
  }

  return (
    <>
      <ContextDebugger />
      <Router>
      {/* Toast notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            style: {
              background: '#10b981',
            },
          },
          error: {
            duration: 4000,
            style: {
              background: '#ef4444',
            },
          },
        }}
      />

      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/definir-senha" element={<PasswordSetup />} />
          <Route path="/perfil" element={<ProfileSettings />} />

          {/* Patient routes - separate from main layout */}
          <Route path="/patient/dashboard" element={<PatientDashboard />} />

          {/* Routes with layout - protected from patient access */}
          <Route element={<NonPatientRoute />}>
            <Route element={<BaseLayout />}>
              {/* Smart redirect based on context */}
              <Route path="/" element={<SmartRedirect />} />

              {/* Main routes */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clinicas" element={<ClinicSelection />} />
              <Route path="/medicos" element={<DoctorSelection />} />

            {/* Calendar route - requires doctor selection */}
            <Route 
              path="/calendar" 
              element={
                <ProtectedRoute requiresDoctor>
                  <CalendarPage />
                </ProtectedRoute>
              } 
            />

            {/* Setup route - Admin only for initial organization setup */}
            <Route
              path="/setup"
              element={
                <AdminRoute>
                  <SetupWizardPage />
                </AdminRoute>
              }
            />

            {/* Admin routes */}
            <Route path="/admin">
              <Route
                path="medicos"
                element={
                  <AdminRoute>
                    <DoctorsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="pacientes"
                element={
                  <AdminRoute>
                    <PatientsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="usuarios"
                element={
                  <AdminRoute>
                    <UsersPage />
                  </AdminRoute>
                }
              />
            </Route>

            {/* Help route */}
            <Route path="/ajuda" element={<HelpPage />} />

            {/* Access denied route */}
            <Route
              path="/acesso-negado"
              element={
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-2">
                      Acesso Negado
                    </h2>
                    <p className="text-gray-600">
                      Você não tem permissão para acessar esta página
                    </p>
                  </div>
                </div>
              }
            />

            {/* 404 route */}
            <Route
              path="*"
              element={
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <h2 className="text-4xl font-bold text-gray-900 mb-2">
                      404
                    </h2>
                    <p className="text-gray-600">Página não encontrada</p>
                  </div>
                </div>
              }
            />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Router>
    </>
  );
}

export default App;